'use client';

import React from 'react';
import { create } from 'zustand';
import { sendCommandToIframe } from '@/utils/iframeMessaging';

type LandingPageModalState = {
  show: boolean;
  nodeId: string;
};

type LandingPageModalStore = {
  landingPageModal: LandingPageModalState;
  setLandingPageModal: (landingPageModal: LandingPageModalState) => void;
  resetLandingPageModal: () => void;
  openModal: (nodeId: string) => void;
  closeModal: () => void;
  isModalOpen: (nodeId: string) => boolean;

  // Element selector state
  isElementSelectorEnabled: boolean;
  toggleElementSelector: () => void;
  setElementSelectorEnabled: (enabled: boolean) => void;
  forceDisableElementSelector: () => void;

  // Iframe reference for communication
  iframeRef: React.RefObject<HTMLIFrameElement | null> | null;
  setIframeRef: (ref: React.RefObject<HTMLIFrameElement | null>) => void;
};

const initialLandingPageModalState: LandingPageModalState = {
  show: false,
  nodeId: '',
};

export const useLandingPageModal = create<LandingPageModalStore>()((set, get) => ({
  landingPageModal: initialLandingPageModalState,
  setLandingPageModal: (landingPageModal: LandingPageModalState) => set({ landingPageModal }),
  resetLandingPageModal: () => set({ landingPageModal: initialLandingPageModalState }),
  openModal: (nodeId: string) => set({ landingPageModal: { show: true, nodeId } }),
  closeModal: () => set({ landingPageModal: initialLandingPageModalState }),
  isModalOpen: (nodeId: string) => {
    const { landingPageModal } = get();
    return landingPageModal.show && landingPageModal.nodeId === nodeId;
  },

  // Element selector state
  isElementSelectorEnabled: true,
  toggleElementSelector: () => {
    const { isElementSelectorEnabled, iframeRef } = get();
    const newState = !isElementSelectorEnabled;

    set({ isElementSelectorEnabled: newState });

    // Send command to iframe to enable/disable edit mode
    if (iframeRef?.current) {
      sendCommandToIframe(iframeRef.current, newState ? 'ENABLE_EDIT_MODE' : 'DISABLE_EDIT_MODE');
    }
  },
  setElementSelectorEnabled: (enabled: boolean) => {
    const { iframeRef } = get();
    set({ isElementSelectorEnabled: enabled });

    // Send command to iframe
    if (iframeRef?.current) {
      sendCommandToIframe(iframeRef.current, enabled ? 'ENABLE_EDIT_MODE' : 'DISABLE_EDIT_MODE');
    }
  },

  // Force disable element selector (for chat loading states)
  forceDisableElementSelector: () => {
    const { iframeRef } = get();
    set({ isElementSelectorEnabled: false });

    // Send disable command to iframe
    if (iframeRef?.current) {
      sendCommandToIframe(iframeRef.current, 'DISABLE_EDIT_MODE');
    }
  },

  // Iframe reference management
  iframeRef: null,
  setIframeRef: (ref: React.RefObject<HTMLIFrameElement | null>) => set({ iframeRef: ref }),
}));
